import type { ClinicianInfoPublic } from "./api";
import type { Chat } from "./chats";
import type { DocumentReference } from "@um/contracts/firestore";
import { getFirestoreContractProvider } from "./FirestoreContractProvider";
import type { WithRef } from "./schema";

export interface LinkedSites {
  www: string;
  understand: string;
  app: string;
  show: string;
  clinician: string;
  studio: string;
  admin: string;
  appNative: string;
}

export interface LinkedEnvironments {
  prod: LinkedSites;
  staging: LinkedSites;
  emulator: LinkedSites;
}

export type ConsoleEnvironment = keyof LinkedEnvironments;

/** GCP / Firebase project IDs from `.firebaserc`  */
export const FIREBASE_GCP_PROJECTS = {
  prod: "uniqueminds-prod",
  staging: "uniqueminds-staging",
} as const;

export function firebaseConsoleOverviewUrl(projectId: string): string {
  return `https://console.firebase.google.com/project/${projectId}/overview`;
}

export function firebaseFirestoreDataUrl(projectId: string): string {
  return `https://console.firebase.google.com/project/${projectId}/firestore/databases/-default-/data`;
}

/** Cloud Functions (where Genkit flows are deployed for hosted envs). */
export function googleCloudFunctionsConsoleUrl(projectId: string): string {
  return `https://console.cloud.google.com/functions/list?project=${projectId}`;
}

/** Ports aligned with repo `firebase.json` emulators and local `genkit start`. */
export const LOCAL_DEV_INFRA = {
  firebaseEmulatorUi: 4040,
  firestoreEmulator: 8080,
  genkitDevUi: 4000,
  /** `firebase emulators:start` functions host. */
  functionsEmulator: 5001,
} as const;

export type InfrastructureLinks = {
  firebaseConsole: string;
  firestore: string;
  genkitUi: string;
};

export function getInfrastructureLinks(env: ConsoleEnvironment): InfrastructureLinks {
  if (env === "emulator") {
    return {
      firebaseConsole: `http://127.0.0.1:${LOCAL_DEV_INFRA.firebaseEmulatorUi}`,
      firestore: `http://127.0.0.1:${LOCAL_DEV_INFRA.firestoreEmulator}`,
      genkitUi: `http://127.0.0.1:${LOCAL_DEV_INFRA.genkitDevUi}`,
    };
  }
  const projectId = FIREBASE_GCP_PROJECTS[env];
  return {
    firebaseConsole: firebaseConsoleOverviewUrl(projectId),
    firestore: firebaseFirestoreDataUrl(projectId),
    genkitUi: googleCloudFunctionsConsoleUrl(projectId),
  };
}

/** Static app base URLs for a target environment (no Firebase contract provider). */
export function getLinkedSites(env: ConsoleEnvironment): Readonly<LinkedSites> {
  return sites[env];
}

const sites: LinkedEnvironments = {
  prod: {
    www: "https://uniqueminds.com",
    understand: "https://understand.uniqueminds.com",
    app: "https://uniqueminds.app",
    show: "https://um.show",
    clinician: "https://uniqueminds.care",
    studio: "https://uniqueminds.studio",
    admin: "https://admin.uniqueminds.com",
    appNative: "https://uniqueminds.onelink.me/7RCO/app",
  },
  staging: {
    www: "https://staging.uniqueminds.com",
    understand: "https://staging.understand.uniqueminds.com",
    app: "https://staging.uniqueminds.app",
    show: "https://staging.um.show",
    clinician: "https://staging.uniqueminds.care",
    studio: "https://uniqueminds-studio-staging.web.app",
    admin: "https://staging.admin.uniqueminds.com",
    appNative: "https://uniqueminds.onelink.me/7RCO/app",
  },
  emulator: {
    www: "http://localhost:7070",
    understand: "http://localhost:7080",
    app: "http://localhost:5173",
    show: "http://localhost:7090",
    clinician: "http://localhost:5555",
    studio: "http://localhost:7090",
    admin: "http://localhost:6099",
    appNative: "https://uniqueminds.onelink.me/7RCO/app",
  },
};

export type Site = keyof LinkedSites;
export const allSites = [
  "www",
  "understand",
  "app",
  "show",
  "clinician",
  "studio",
  "admin",
  "appNative",
] as const satisfies readonly Site[];

export const getBaseUrl = (site: Site, env?: keyof LinkedEnvironments, fromSite?: Site): string => {
  const fsp = getFirestoreContractProvider();
  env = env || (fsp.isEmulator() ? "emulator" : fsp.isProduction() ? "prod" : "staging");

  if (fromSite && site == fromSite && !window.location.origin.startsWith("capacitor://")) {
    return window.location.origin;
  }
  return fsp.getEnv(`URL_${site.toUpperCase()}_${env.toUpperCase()}`) || sites[env][site];
};

export function getMindUrl(mindId: DocumentReference, fromSite?: Site): string {
  return `${getBaseUrl("admin", undefined, fromSite)}/minds/${mindId.parent?.parent?.id}/${mindId.id}`;
}

export function getManualUrl(manualId: DocumentReference, fromSite?: Site): string {
  return `${getBaseUrl("admin", undefined, fromSite)}/manuals/${manualId.parent?.parent?.id}/${manualId.id}`;
}

export function getAnimalUrl(animalId: DocumentReference, personaId?: string, fromSite?: Site): string {
  return `${getBaseUrl("admin", undefined, fromSite)}/animal/${animalId.parent?.parent?.id}/${animalId.id}`;
}

export function getChatUrl(chat: WithRef<Chat>, fromSite?: Site): string {
  return `${getBaseUrl("admin", undefined, fromSite)}/chats/${chat.ref.parent?.parent?.id}/${chat.ref.id}`;
}

export function getInviteUrl(inviteCode: string, fromSite?: Site): string {
  return `${getBaseUrl("app", undefined, fromSite)}/join/${inviteCode}`;
}

/** Discovery screen, or a short invitation / pairing link on the show site. */
export function getShowUrl(code?: string, fromSite?: Site): string {
  const base = getBaseUrl("show", undefined, fromSite).replace(/\/$/, "");
  return code ? `${base}/${encodeURIComponent(code.trim().toLowerCase())}` : base;
}

export function getClinicianLandingPageUrl(clinicianCode: string, fromSite?: Site): string {
  return `${getBaseUrl("clinician", undefined, fromSite)}/${clinicianCode}`;
}

/** Link an end user opens to redeem a wholesaler purchase code in the app. */
export function getRedeemPurchaseUrl(code: string, fromSite?: Site): string {
  return `${getBaseUrl("app", undefined, fromSite)}/redeem/${code}`;
}

/** Ready-to-send message a wholesaler can forward to the person redeeming a code. */
export function buildRedemptionInstructions(code: string, fromSite?: Site): string {
  const redeemUrl = getRedeemPurchaseUrl(code, fromSite);
  return [
    "You've been given access to Unique Minds!",
    "",
    "To activate your access:",
    `1. Open this link: ${redeemUrl}`,
    "2. Sign in or create your account.",
    `3. Tap "Redeem to this account" to unlock your access.`,
  ].join("\n");
}

/** Wholesaler portal base URL on the www site. */
export function getWholesalerPortalUrl(fromSite?: Site): string {
  return `${getBaseUrl("www", undefined, fromSite)}/wholesaler`;
}

export function getUtmParams(clinician: ClinicianInfoPublic, testGroupId?: string | null) {
  testGroupId = testGroupId || clinician.testResourceGroup;
  return new URLSearchParams({
    utm_source: "clinician",
    utm_medium: "referral",
    utm_campaign: clinician.clinicianType,
    utm_id: clinician.code,
    ...(testGroupId ? { test: testGroupId } : {}), // propogate the test group to the other app
  });
}
